import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Search } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function OrderTrackingPage() {
  const [orderId, setOrderId] = useState('');
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [order, setOrder] = useState(null);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Prefill from ?order_id=...&email=... when admin emails link here
  useEffect(() => {
    const qOrderId = searchParams.get('order_id');
    const qEmail = searchParams.get('email');
    if (qOrderId) setOrderId(qOrderId);
    if (qEmail) setEmail(qEmail);
  }, [searchParams]);

  const handleTrack = async (e) => {
    e.preventDefault();
    if (!orderId.trim() || !email.trim()) { toast.error('Please enter both fields'); return; }
    setLoading(true);
    try {
      const res = await fetch(`${API}/orders/track`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ order_id: orderId.trim().toUpperCase(), email: email.trim() }),
      });
      if (res.ok) {
        const data = await res.json();
        setOrder(data);
        navigate(`/order/${data.order_id}?email=${encodeURIComponent(email.trim())}`);
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Order not found');
      }
    } catch { toast.error('Something went wrong'); }
    setLoading(false);
  };

  return (
    <div className="pt-20 sm:pt-[122px] min-h-screen bg-cream" data-testid="order-tracking-page">
      <section className="bg-white py-10 sm:py-16 border-b border-cream-dark">
        <div className="max-w-[1632px] mx-auto px-5 sm:px-8 lg:px-12">
          <h1 className="text-4xl sm:text-5xl font-display font-semibold text-[#1A1A1A] mb-4 tracking-tight">Track Your Order</h1>
          <p className="text-base sm:text-lg text-gray-500 max-w-2xl font-body">Enter your order number and email to view your order status.</p>
        </div>
      </section>

      <div className="flex items-center justify-center px-4 py-6 sm:py-12">
      <div className="w-full max-w-md">
        <div className="bg-white rounded-2xl shadow-card p-6 sm:p-8">
          <form onSubmit={handleTrack} className="space-y-4">
            <div>
              <Label className="text-sm font-medium">Order ID</Label>
              <Input
                type="text" required placeholder="e.g. A3X7K2"
                value={orderId} onChange={e => setOrderId(e.target.value)}
                className="mt-1 h-11 uppercase" data-testid="track-order-id"
              />
            </div>
            <div>
              <Label className="text-sm font-medium">Email Address</Label>
              <Input
                type="email" required placeholder="you@email.com"
                value={email} onChange={e => setEmail(e.target.value)}
                className="mt-1 h-11" data-testid="track-email"
              />
            </div>
            <Button type="submit" className="w-full h-11 bg-bush hover:bg-bush/90 text-white font-medium" disabled={loading} data-testid="track-submit-btn">
              {loading ? 'Searching...' : 'Track Order'}
            </Button>
          </form>
        </div>
      </div>
      </div>
    </div>
  );
}
